"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildTrade = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const types_1 = require("@shapeshiftoss/types");
const api_1 = require("../../../api");
const constants_1 = require("../../utils/constants");
const getTradeQuote_1 = require("../getThorTradeQuote/getTradeQuote");
const getThorTxData_1 = require("../utils/bitcoin/utils/getThorTxData");
const getCosmosTxData_1 = require("../utils/cosmos/getCosmosTxData");
const makeTradeTx_1 = require("../utils/evm/makeTradeTx");
const buildTrade = async ({ deps, input }) => {
    var _a, _b, _c, _d;
    try {
        const { buyAsset, receiveAddress: destinationAddress, sellAmountBeforeFeesCryptoBaseUnit: sellAmountCryptoBaseUnit, sellAsset, bip44Params, slippage: slippageTolerance = constants_1.DEFAULT_SLIPPAGE, wallet, sendMax, } = input;
        const quote = await (0, getTradeQuote_1.getThorTradeQuote)({ deps, input });
        const sellAdapter = deps.adapterManager.get(sellAsset.chainId);
        if (!sellAdapter)
            throw new api_1.SwapError('[buildTrade]: unsupported sell asset', {
                code: api_1.SwapErrorTypes.BUILD_TRADE_FAILED,
                fn: 'buildTrade',
                details: { sellAsset },
            });
        const { chainNamespace } = (0, caip_1.fromAssetId)(sellAsset.assetId);
        if (chainNamespace === caip_1.CHAIN_NAMESPACE.Evm) {
            const ethTradeTx = await (0, makeTradeTx_1.makeTradeTx)({
                wallet,
                slippageTolerance,
                bip44Params,
                sellAsset,
                buyAsset,
                adapter: sellAdapter,
                sellAmountCryptoBaseUnit,
                destinationAddress,
                deps,
                gasPriceCryptoBaseUnit: (_b = (_a = quote.feeData.chainSpecific) === null || _a === void 0 ? void 0 : _a.gasPriceCryptoBaseUnit) !== null && _b !== void 0 ? _b : '0',
                gasLimit: (_d = (_c = quote.feeData.chainSpecific) === null || _c === void 0 ? void 0 : _c.estimatedGas) !== null && _d !== void 0 ? _d : '0',
                buyAssetTradeFeeUsd: quote.feeData.buyAssetTradeFeeUsd,
            });
            return {
                chainId: sellAsset.chainId,
                ...quote,
                receiveAddress: destinationAddress,
                txData: ethTradeTx.txToSign,
            };
        }
        else if (chainNamespace === caip_1.CHAIN_NAMESPACE.Utxo) {
            const { vault, opReturnData } = await (0, getThorTxData_1.getThorTxInfo)({
                deps,
                sellAsset,
                buyAsset,
                sellAmountCryptoBaseUnit,
                slippageTolerance,
                destinationAddress,
                xpub: input.xpub,
                buyAssetTradeFeeUsd: quote.feeData.buyAssetTradeFeeUsd,
            });
            const buildTxResponse = await sellAdapter.buildSendTransaction({
                value: sellAmountCryptoBaseUnit,
                wallet,
                to: vault,
                bip44Params: input.bip44Params,
                chainSpecific: {
                    accountType: input.accountType,
                    satoshiPerByte: quote.feeData.chainSpecific
                        .satsPerByte,
                    opReturnData,
                },
                sendMax,
            });
            return {
                chainId: sellAsset.chainId,
                ...quote,
                receiveAddress: destinationAddress,
                txData: buildTxResponse.txToSign,
            };
        }
        else if (chainNamespace === caip_1.CHAIN_NAMESPACE.CosmosSdk) {
            if (!bip44Params) {
                throw new Error('bip44Params required as input');
            }
            const txData = await (0, getCosmosTxData_1.getCosmosTxData)({
                bip44Params,
                deps,
                sellAdapter: sellAdapter,
                sellAmountCryptoBaseUnit,
                sellAsset,
                slippageTolerance,
                chainId: input.chainId,
                buyAsset,
                wallet,
                destinationAddress,
                quote: quote,
            });
            return {
                chainId: types_1.KnownChainIds.CosmosMainnet,
                ...quote,
                receiveAddress: destinationAddress,
                txData,
            };
        }
        else {
            throw new api_1.SwapError('[buildTrade]: unsupported chain id', {
                code: api_1.SwapErrorTypes.BUILD_TRADE_FAILED,
                fn: 'buildTrade',
                details: { sellAsset },
            });
        }
    }
    catch (e) {
        if (e instanceof api_1.SwapError)
            throw e;
        throw new api_1.SwapError('[buildTrade]: error building trade', {
            code: api_1.SwapErrorTypes.BUILD_TRADE_FAILED,
            fn: 'buildTrade',
            cause: e,
        });
    }
};
exports.buildTrade = buildTrade;
